import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

/**
 * The workspace's two draggable dividers: one on the rail's right edge, one
 * between the list pane and the detail pane.
 *
 * The mail screen and the calls screen are the same workspace shape, so the
 * behaviour is written once here and built twice. Four details are worth
 * knowing:
 *
 *  - a size is only ever written as a custom property on the GRID
 *    (`--crm-rail-col-w`, `--crm-pane-w`, `--crm-pane-h`), never on a column.
 *    The columns are grid tracks, and a width on the child does not move the
 *    track it sits in.
 *  - each handle is positioned in pixels, not by grid placement. The list track
 *    ships as a fraction, and CSS cannot resolve a fraction into a length for an
 *    absolutely positioned sibling, so its offset is measured and written after
 *    every layout change. Both handles are placed by the same call, because
 *    widening the rail also moves the list's far edge.
 *  - the panes are re-measured through a ResizeObserver rather than at mount.
 *    A route that has not been visited yet has been laid out at zero width, and
 *    the first honest measurement only arrives once it is on screen.
 *  - the rail divider is always vertical, but the one between the list and the
 *    detail pane follows `layout`: sideways with the pane beside the list,
 *    up-and-down with the pane under it. The two placements keep SEPARATE
 *    sizes (`--crm-pane-w` and `--crm-pane-h`), which is not an oversight - a
 *    column dragged to 420px says nothing about how tall the list should be,
 *    and carrying one number across would throw away a choice rather than
 *    honour it.
 */
export interface SplitterOptions {
  /** Smallest the rail may get, in pixels. */
  minRail: number;
  /** Smallest the list pane may get across, in pixels. */
  minLeft: number;
  /** Room the detail pane keeps for itself across, in pixels. */
  minRight: number;
  /**
   * Where the detail pane sits. "right" is the three-column shape; "bottom"
   * stacks it under the list. Omit and the screen never offers the choice.
   */
  layout?: "right" | "bottom";
  /** Smallest the list may get down the screen, when the pane is under it. */
  minTop?: number;
  /** Room the detail pane keeps down the screen, when it is under the list. */
  minBottom?: number;
}

/** The grid's own properties. All three fall back to what the layout ships. */
const RAIL_VAR = "--crm-rail-col-w";
const PANE_W_VAR = "--crm-pane-w";
const PANE_H_VAR = "--crm-pane-h";

/** The gutter between two tracks. Read rather than assumed - the compact
 *  density narrows it, and a divider placed against a stale gap drifts. */
function gapOf(grid: HTMLElement, axis: "x" | "y"): number {
  const cs = getComputedStyle(grid);
  return parseFloat(axis === "x" ? cs.columnGap : cs.rowGap) || 16;
}

/**
 * One divider's pointer, keyboard and reset wiring.
 *
 * Two of these are built per screen and the only things that differ are which
 * pane they measure and which way they travel, so the difference is an axis
 * plus a callback rather than a branch per behaviour: the hook hands the new
 * size to `setSize`, which is where each divider's own limits live.
 */
function useDivider(
  paneRef: React.RefObject<HTMLDivElement>,
  handleRef: React.RefObject<HTMLButtonElement>,
  axis: "x" | "y",
  setSize: (px: number) => void,
  reset: () => void,
) {
  const dragRef = useRef<{ at: number; size: number } | null>(null);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const pane = paneRef.current;
      const handle = handleRef.current;
      if (!pane || !handle) return;
      e.preventDefault();
      const box = pane.getBoundingClientRect();
      dragRef.current = {
        at: axis === "x" ? e.clientX : e.clientY,
        size: axis === "x" ? box.width : box.height,
      };
      handle.setPointerCapture(e.pointerId);
      handle.classList.add("is-on");
      document.body.classList.add(axis === "x" ? "crm-colresize" : "crm-rowresize");
    },
    [axis, handleRef, paneRef],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLButtonElement>) => {
      const drag = dragRef.current;
      if (drag) setSize(drag.size + ((axis === "x" ? e.clientX : e.clientY) - drag.at));
    },
    [axis, setSize],
  );

  const endDrag = useCallback(() => {
    dragRef.current = null;
    handleRef.current?.classList.remove("is-on");
    document.body.classList.remove("crm-colresize", "crm-rowresize");
  }, [handleRef]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      const back = axis === "x" ? "ArrowLeft" : "ArrowUp";
      const forth = axis === "x" ? "ArrowRight" : "ArrowDown";
      if (e.key !== back && e.key !== forth) return;
      const pane = paneRef.current;
      if (!pane) return;
      e.preventDefault();
      const box = pane.getBoundingClientRect();
      const step = (e.shiftKey ? 48 : 16) * (e.key === forth ? 1 : -1);
      setSize((axis === "x" ? box.width : box.height) + step);
    },
    [axis, paneRef, setSize],
  );

  return {
    ref: handleRef,
    className: "crm-splitter",
    "data-axis": axis,
    role: "separator" as const,
    "aria-orientation": (axis === "x" ? "vertical" : "horizontal") as "vertical" | "horizontal",
    onPointerDown,
    onPointerMove,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
    // Back to the proportions the page ships with, the way a window divider
    // resets. Removing the property rather than writing the default back: the
    // default is the density's and the layout's, and none of them share a
    // number.
    onDoubleClick: reset,
    onKeyDown,
  };
}

export function useSplitter({
  minRail,
  minLeft,
  minRight,
  layout = "right",
  minTop = 180,
  minBottom = 260,
}: SplitterOptions) {
  const gridRef = useRef<HTMLDivElement | null>(null);
  const railRef = useRef<HTMLDivElement | null>(null);
  const leftRef = useRef<HTMLDivElement | null>(null);
  const rightRef = useRef<HTMLDivElement | null>(null);
  const railHandleRef = useRef<HTMLButtonElement | null>(null);
  const handleRef = useRef<HTMLButtonElement | null>(null);
  const stacked = layout === "bottom";

  /** Both handles, back onto the edges they divide. */
  const place = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const origin = grid.getBoundingClientRect();

    // The rail's divider never changes direction: the rail is a column in both
    // layouts, and in the stacked one it is the column the other two share a
    // track beside.
    const rail = railRef.current;
    const railHandle = railHandleRef.current;
    if (rail && railHandle) railHandle.style.left = `${rail.getBoundingClientRect().right - origin.left}px`;

    const list = leftRef.current;
    const handle = handleRef.current;
    if (!list || !handle) return;
    const box = list.getBoundingClientRect();
    // The unused offset is REMOVED rather than zeroed. The stylesheet pins the
    // stacked handle to the grid's right edge, and a leftover inline `left`
    // from the side-by-side layout would beat the `right: 0` it is paired with.
    if (stacked) {
      handle.style.top = `${box.bottom - origin.top}px`;
      handle.style.left = `${box.left - origin.left}px`;
    } else {
      handle.style.left = `${box.right - origin.left}px`;
      handle.style.removeProperty("top");
    }
  }, [stacked]);

  const setRailWidth = useCallback(
    (w: number) => {
      const grid = gridRef.current;
      if (!grid) return;
      const gap = gapOf(grid, "x");
      // What the rail may not take. Read off the layout and off which panes are
      // actually mounted rather than assumed to be all three: stacked, the list
      // and the reading pane share ONE track beside the rail, and the calls
      // reports (do-not-call, cost, coverage) put a single panel there with no
      // list at all. Charging either of them for a column that is not there
      // would stop the rail well short of the room it really has.
      const rest = stacked
        ? gap + Math.max(minLeft, minRight)
        : leftRef.current && rightRef.current
          ? gap + minLeft + gap + minRight
          : gap + minRight;
      const max = Math.max(minRail, grid.getBoundingClientRect().width - rest);
      grid.style.setProperty(RAIL_VAR, `${Math.max(minRail, Math.min(max, w))}px`);
      place();
    },
    [minLeft, minRail, minRight, place, stacked],
  );

  const setPaneSize = useCallback(
    (size: number) => {
      const grid = gridRef.current;
      const list = leftRef.current;
      const pane = rightRef.current;
      if (!grid || !list || !pane) return;
      // Measured off the reading pane's own far edge, so the limit stays
      // correct even when the tracks around it change - a widened rail
      // included, since the list's near edge moves with it.
      const axis = stacked ? "y" : "x";
      const gap = gapOf(grid, axis);
      const lo = stacked ? minTop : minLeft;
      const paneBox = pane.getBoundingClientRect();
      const listBox = list.getBoundingClientRect();
      const room = stacked
        ? paneBox.bottom - listBox.top - gap - minBottom
        : paneBox.right - listBox.left - gap - minRight;
      const max = Math.max(lo, room);
      grid.style.setProperty(stacked ? PANE_H_VAR : PANE_W_VAR, `${Math.max(lo, Math.min(max, size))}px`);
      place();
    },
    [minBottom, minLeft, minRight, minTop, place, stacked],
  );

  const resetRail = useCallback(() => {
    gridRef.current?.style.removeProperty(RAIL_VAR);
    place();
  }, [place]);

  const resetPane = useCallback(() => {
    gridRef.current?.style.removeProperty(stacked ? PANE_H_VAR : PANE_W_VAR);
    place();
  }, [place, stacked]);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const ro = new ResizeObserver(place);
    ro.observe(grid);
    return () => ro.disconnect();
  }, [place]);

  // Switching the reading pane from beside the list to under it leaves the grid
  // exactly the same size, so the observer above never fires - and the divider
  // would keep the offset it had in the layout before. This is the call that
  // moves it.
  useLayoutEffect(() => {
    place();
  }, [place]);

  const railHandleProps = useDivider(railRef, railHandleRef, "x", setRailWidth, resetRail);
  const handleProps = useDivider(leftRef, handleRef, stacked ? "y" : "x", setPaneSize, resetPane);

  return { gridRef, railRef, leftRef, rightRef, railHandleProps, handleProps };
}
